import React, { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import { Autocomplete, Box, Button, Chip, InputBase, Menu, MenuItem, Stack, TextField } from '@mui/material';

import { useGetReportingLensFields } from '~/hooks/apis/reporting/useLensAuthoring';

import { BlockSql } from './BlockQuery';
import type { BoardOperation } from './editor';
import FieldList, { AGGREGATES, type Field, fieldsOf, isDimension, isMeasure, measureFromNumber, startFieldDrag, useFieldDrop } from './fields';
import { conditionText, humanise, presetLabel } from './format';
import { Choice, FIELD, Picker, Reveal, Row, Section } from './settingsKit';
import type { Block, BlockFamily, Condition, KpiItem, LensDocumentData, LensState, Param } from './types';
import type { BoardEditor } from './useBoardEdit';

type QuerySettingsProps = { block: Block; kind: string; family?: BlockFamily; data?: LensDocumentData; editor: BoardEditor; namedQueries?: Array<string>; params?: Record<string, Param>; state?: LensState; between?: React.ReactNode };

type Where = Record<string, Condition>;

const LENS = 'lens';
const CUSTOM = 'custom';

const WINDOWS: Array<[string, string]> = [
  [LENS, 'Lens period'],
  ['13w', '13 weeks'],
  ['52w', '52 weeks'],
  ['12m', '12 months'],
  ['365d', '365 days'],
  ['all', 'All time'],
];

const GRAINS: Array<[string, string]> = [
  ['minute', 'Minute'],
  ['hour', 'Hour'],
  ['day', 'Day'],
  ['week', 'Week'],
  ['month', 'Month'],
  ['quarter', 'Quarter'],
  ['year', 'Year'],
];

const COMPARES: Array<[string, string]> = [
  [LENS, 'Lens'],
  ['none', 'None'],
  ['prior', 'Prior'],
  ['ly', 'Last year'],
  ['l2y', 'Two years ago'],
  ['prior,ly', 'Both'],
];

const FIXED = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/;

const DATE_INPUT = { bgcolor: 'action.hover', border: '1px solid transparent', borderRadius: '8px', flex: 1, fontSize: 13, minHeight: 28, minWidth: 0, px: 1, '&.Mui-focused': { bgcolor: 'background.paper', borderColor: 'primary.main' } } as const;

function FixedDates({ window, disabled, onApply }: { window?: string; disabled: boolean; onApply: (window: string) => void }) {
  const fixed = FIXED.exec(window ?? '');
  const [from, setFrom] = useState(fixed?.[1] ?? '');
  const [to, setTo] = useState(fixed?.[2] ?? '');
  const ready = Boolean(from && to && from <= to) && `${from}..${to}` !== window;
  return (
    <Row label="Dates" set={Boolean(fixed)}>
      <InputBase inputProps={{ 'aria-label': 'Fixed from' }} onChange={(event) => setFrom(event.target.value)} sx={DATE_INPUT} type="date" value={from} />
      <InputBase inputProps={{ 'aria-label': 'to' }} onChange={(event) => setTo(event.target.value)} sx={DATE_INPUT} type="date" value={to} />
      <Button disabled={disabled || !ready} onClick={() => onApply(`${from}..${to}`)} size="small" sx={{ minWidth: 0 }} variant="text">
        Use
      </Button>
    </Row>
  );
}

const OPERATORS: Array<[string, string]> = [
  ['is', 'is'],
  ['not', 'isn’t'],
  ['min', 'at least'],
  ['max', 'at most'],
  ['between', 'between'],
];

export function WhereAdder({ columns, numbers = [], options, onAdd, disabled, initialColumn = '' }: { columns: Array<[string, string]>; numbers?: Array<[string, string]>; options?: Record<string, Array<string>>; onAdd: (column: string, condition: Condition) => void; disabled: boolean; initialColumn?: string }) {
  const [column, setColumn] = useState(initialColumn);
  const [operator, setOperator] = useState('is');
  const [values, setValues] = useState<Array<string>>([]);
  const [range, setRange] = useState({ min: '', max: '' });
  const numeric = numbers.some(([name]) => name === column);
  const sides = (operator === 'between' ? ['min', 'max'] : operator === 'min' || operator === 'max' ? [operator] : []) as Array<'min' | 'max'>;
  const bounds = Object.fromEntries(sides.filter((side) => range[side].trim() !== '' && Number.isFinite(Number(range[side]))).map((side) => [side, Number(range[side])]));
  const ready = sides.length ? Object.keys(bounds).length === sides.length : values.length > 0;
  const field = { '& .MuiOutlinedInput-root': { ...FIELD, bgcolor: 'action.hover', py: '1px !important' }, '& fieldset': { border: 0 } };
  const pickColumn = (next: string) => {
    setColumn(next);
    if (!numbers.some(([name]) => name === next) && sides.length) setOperator('is');
  };
  return (
    <Stack spacing={0.75} sx={{ width: '100%' }}>
      <Stack alignItems="center" direction="row" spacing={0.75}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Picker choices={[...columns, ...numbers]} label="Only rows where" onChange={pickColumn} placeholder="Only rows where…" value={column} />
        </Box>
        <Box sx={{ width: 112 }}>
          <Picker choices={numeric ? OPERATORS : OPERATORS.slice(0, 2)} label="Condition" onChange={setOperator} value={operator} />
        </Box>
      </Stack>
      <Stack alignItems="flex-start" direction="row" spacing={0.75}>
        {sides.length ? (
          sides.map((side) => (
            <TextField
              fullWidth
              inputProps={{ 'aria-label': side === 'min' ? 'At least' : 'At most' }}
              key={side}
              onChange={(event) => setRange({ ...range, [side]: event.target.value })}
              placeholder={operator === 'between' ? (side === 'min' ? 'From' : 'To') : 'Value'}
              size="small"
              sx={field}
              type="number"
              value={range[side]}
            />
          ))
        ) : (
          <Autocomplete
            freeSolo
            fullWidth
            multiple
            onChange={(_, next) => setValues(next.map(String))}
            options={options?.[column] ?? []}
            renderInput={(params) => <TextField {...params} inputProps={{ ...params.inputProps, 'aria-label': 'Values' }} placeholder={values.length ? undefined : 'Values'} size="small" sx={field} />}
            size="small"
            value={values}
          />
        )}
        <Button
          disabled={disabled || !column || !ready}
          onClick={() => {
            onAdd(column, sides.length ? bounds : operator === 'not' ? { not: values } : values);
            setColumn('');
            setOperator('is');
            setValues([]);
            setRange({ min: '', max: '' });
          }}
          size="small"
          sx={{ flexShrink: 0, minWidth: 0 }}
          variant="text"
        >
          Add the condition
        </Button>
      </Stack>
    </Stack>
  );
}

function Shelf({ label, set, holds, accepts, onField, stacked, children }: { label: string; set?: boolean; holds?: Field; accepts: (field: Field) => boolean; onField: (field: Field, target: HTMLElement) => void; stacked?: boolean; children: React.ReactNode }) {
  const drop = useFieldDrop((field, target) => accepts(field) && onField(field, target));
  return (
    <Box
      {...drop.props}
      data-shelf={label}
      draggable={Boolean(holds)}
      onDragStart={holds ? (event) => startFieldDrag(event, holds) : undefined}
      sx={{ borderRadius: '8px', transition: 'background-color 120ms', ...(holds ? { '& [data-setting-row] > div:first-of-type': { cursor: 'grab' } } : {}), ...(drop.over ? { bgcolor: 'action.hover' } : {}) }}
    >
      <Row label={label} set={set} stacked={stacked}>
        {children}
      </Row>
    </Box>
  );
}

function ShelfChips({ refs, labelOf, onRemove, choices, onAdd, addLabel, disabled }: { refs: Array<string>; labelOf: (ref: string) => string; onRemove?: (ref: string) => void; choices: Array<[string, string]>; onAdd: (ref: string) => void; addLabel: string; disabled: boolean }) {
  return (
    <Stack spacing={0.75} sx={{ width: '100%' }}>
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {refs.map((ref) => (
          <Chip key={ref} label={labelOf(ref)} onDelete={onRemove && refs.length > 1 ? () => onRemove(ref) : undefined} size="small" sx={{ fontSize: 12.5 }} />
        ))}
      </Stack>
      <Picker choices={choices.filter(([id]) => !refs.includes(id))} disabled={disabled} label={addLabel} onChange={onAdd} placeholder={`${addLabel}…`} value="" />
    </Stack>
  );
}

function useQueryVocabulary(data?: LensDocumentData, params?: Record<string, Param>) {
  const fields = useGetReportingLensFields(data?.lensId ?? '', data?.lensVersion);
  const all = fieldsOf(fields.data);
  const measures = all.filter(isMeasure).map((field): [string, string] => [field.ref, field.label]);
  const dimensions = [...new Map(all.filter(isDimension).map((field) => [field.ref, field.label] as const)).entries()];
  const numberColumns = [...new Map(all.filter((field) => field.kind === 'number' && field.column).map((field) => [field.column!, field.label] as const)).entries()];
  const named = (ref: string) => (ref.startsWith('$') ? `${params?.[ref.slice(1)]?.label ?? humanise(ref.slice(1))} (switch)` : humanise(ref));
  const labelOf = (ref: string) => measures.find(([id]) => id === ref || ref.endsWith(`.${id}`))?.[1] ?? named(ref);
  const columnLabel = (name: string) => [...dimensions, ...numberColumns].find(([column]) => column === name)?.[1] ?? named(name);
  return { fields, all, measures, dimensions, numberColumns, labelOf, columnLabel };
}

export function QuerySummary({ block, kind, data, params }: { block: Block; kind: string; data?: LensDocumentData; params?: Record<string, Param> }) {
  const { labelOf, columnLabel } = useQueryVocabulary(data, params);
  const value = block[kind];
  const shown = typeof value === 'string' ? [value] : Array.isArray(value) ? (value as Array<unknown>).map((entry) => (typeof entry === 'string' ? entry : (entry as KpiItem).kpi)) : [];
  const by = Array.isArray(block.by) ? block.by : [];
  const parts = [
    shown.length > 2 ? `${shown.length} measures` : shown.map((ref) => (kind === 'box' ? humanise(ref.slice(ref.indexOf('.') + 1)) : labelOf(ref))).join(', '),
    by.length ? `by ${by.map(columnLabel).join(', ')}` : '',
    typeof block.across === 'string' ? `across ${columnLabel(block.across)}` : '',
    block.grain ? String(block.grain) : '',
    block.window ? WINDOWS.find(([id]) => id === block.window)?.[1] ?? presetLabel(String(block.window)) : '',
    block.where ? `${Object.keys(block.where).length} condition${Object.keys(block.where).length > 1 ? 's' : ''}` : '',
  ];
  return <>{parts.filter(Boolean).join(' · ')}</>;
}

export default function QuerySettings({ block, kind, family, data, editor, namedQueries = [], params, state, between }: QuerySettingsProps) {
  const [showingSql, setShowingSql] = useState(false);
  const [customDates, setCustomDates] = useState(false);
  const { fields, all, measures, dimensions, numberColumns, labelOf, columnLabel } = useQueryVocabulary(data, params);
  const set = (patch: Record<string, unknown>) => editor.apply({ op: 'set', id: block.id, patch });
  const value = block[kind];
  const list = Array.isArray(value) ? (value as Array<unknown>).map((entry) => (typeof entry === 'string' ? entry : (entry as KpiItem | undefined)?.kpi)).filter((entry): entry is string => typeof entry === 'string') : undefined;
  const by = Array.isArray(block.by) ? block.by : [];
  const where = (block.where ?? {}) as Where;
  const compareEntries = Array.isArray(block.compare) ? block.compare : undefined;
  const compareValues = compareEntries?.filter((entry): entry is 'prior' | 'ly' | 'l2y' => typeof entry === 'string') ?? [];
  const compare = block.compare == null ? LENS : block.compare === 'none' ? 'none' : compareEntries ? (compareValues.length === compareEntries.length ? compareValues.join(',') || 'none' : CUSTOM) : CUSTOM;
  const compareChoices: Array<[string, string]> = COMPARES.some(([value]) => value === compare)
    ? COMPARES
    : [[compare, compare === CUSTOM ? 'Custom comparison' : `Current: ${compareValues.map((value) => COMPARES.find(([key]) => key === value)?.[1] ?? value).join(' + ')}`], ...COMPARES];
  const splits = (family === 'time' && kind !== 'calendar') || family === 'breakdown' || family === 'table' || family === 'bridge';
  const thenBy = (kind === 'table' || kind === 'heatmap' || (family === 'breakdown' && kind !== 'pie')) && Boolean(by[0]);
  const dimensionField = (ref?: string): Field | undefined => (ref ? all.find((field) => field.ref === ref && isDimension(field)) ?? { ref, label: columnLabel(ref), kind: 'dimension' } : undefined);
  const numbers = all.filter((field) => field.kind === 'number');
  const [aggregating, setAggregating] = useState<{ anchor: HTMLElement; field: Field; place: (ref: string) => Array<BoardOperation> }>();
  const [whereColumn, setWhereColumn] = useState('');
  const taken = new Set([...(fields.data?.measures ?? []).map((measure) => measure.id), ...measures.map(([id]) => id)]);
  const placeSingle = (ref: string): Array<BoardOperation> => [{ op: 'set', id: block.id, patch: { [kind]: ref } }];
  const placeInList = (ref: string): Array<BoardOperation> => (list?.includes(ref) ? [] : [{ op: 'set', id: block.id, patch: { [kind]: [...(value as Array<unknown>), ref] } }]);
  const placeMeasure = list ? placeInList : placeSingle;
  const measureInto = (field: Field, anchor: HTMLElement, place: (ref: string) => Array<BoardOperation>) => {
    if (isMeasure(field)) editor.apply(place(field.ref));
    else if (field.kind === 'number') setAggregating({ anchor, field, place });
  };
  const splitWith = (field: Field) => {
    if (!splits || !isDimension(field) || by.includes(field.ref)) return;
    set({ by: !by[0] ? [field.ref] : thenBy && by.length < 2 ? [by[0], field.ref] : [field.ref, ...by.slice(1)] });
  };
  const pick = (field: Field, anchor: HTMLElement) => {
    if (kind === 'box' && field.kind === 'number') set({ box: field.ref });
    else if (isMeasure(field) || field.kind === 'number') measureInto(field, anchor, placeMeasure);
    else if (splits) splitWith(field);
    else setWhereColumn(field.ref);
  };
  const blockText = JSON.stringify(block);
  const used = new Set(all.filter((field) => blockText.includes(`"${field.ref}"`) || blockText.includes(`.${field.ref}"`)).map((field) => field.ref));
  const datasets = fields.data?.datasets ?? [];

  return (
    <>
      <Section defaultOpen id="query" summary={<QuerySummary block={block} data={data} kind={kind} params={params} />} title="Query">
        {namedQueries.length && kind !== 'calendar' ? (
          <Row label="Reads" set={typeof block.query === 'string'}>
            <Picker choices={[['', 'Its own query'], ...namedQueries.map((name): [string, string] => [name, `Named query ${name}`])]} disabled={editor.pending} label="Reads" onChange={(query) => set({ query: query || null })} value={typeof block.query === 'string' ? block.query : ''} />
          </Row>
        ) : null}
        {kind === 'box' && typeof value === 'string' ? (
          <Shelf accepts={(field) => field.kind === 'number'} label="Spread of" onField={(field) => set({ box: field.ref })}>
            <Picker
              choices={[...(numbers.some((field) => field.ref === value) ? [] : [[value, value] as [string, string]]), ...numbers.map((field): [string, string] => [field.ref, `${field.label} · ${datasets.find((dataset) => dataset.id === field.dataset)?.label ?? field.dataset}`])]}
              disabled={editor.pending}
              label="Spread of"
              onChange={(box) => set({ box })}
              value={value}
            />
          </Shelf>
        ) : null}
        {kind !== 'kpis' && kind !== 'box' && typeof value === 'string' ? (
          <Shelf accepts={(field) => isMeasure(field) || field.kind === 'number'} label="Measure" onField={(field, target) => measureInto(field, target, placeSingle)}>
            <Picker choices={[...(measures.some(([id]) => id === value) ? [] : [[value, labelOf(value)] as [string, string]]), ...measures]} disabled={editor.pending} label="Measure" onChange={(ref) => set({ [kind]: ref })} value={value} />
          </Shelf>
        ) : null}
        {list ? (
          <Shelf accepts={(field) => isMeasure(field) || field.kind === 'number'} label={kind === 'kpis' ? 'Figures' : kind === 'table' ? 'Values' : 'Measures'} onField={(field, target) => measureInto(field, target, placeInList)} stacked>
            <ShelfChips
              addLabel={kind === 'kpis' ? 'Add a figure' : 'Add a measure'}
              choices={measures}
              disabled={editor.pending}
              labelOf={labelOf}
              onAdd={(ref) => editor.apply(placeInList(ref))}
              onRemove={(ref) => (kind === 'kpis' || kind === 'table' ? editor.apply({ op: 'remove', id: `${block.id}/${ref}` }) : set({ [kind]: list.filter((entry) => entry !== ref) }))}
              refs={list}
            />
          </Shelf>
        ) : null}
        {splits ? (
          <Shelf accepts={isDimension} holds={dimensionField(by[0])} label="Dimension 1" onField={(field) => field.ref !== by[0] && set({ by: field.ref === by[1] ? [by[1], by[0]] : [field.ref, ...by.slice(1)] })} set={Boolean(by[0])}>
            <Picker
              choices={[['', family === 'time' ? 'No split' : 'One total'], ...(by[0] && !dimensions.some(([name]) => name === by[0]) ? [[by[0], columnLabel(by[0])] as [string, string]] : []), ...dimensions]}
              disabled={editor.pending}
              label="Dimension 1"
              onChange={(next) => set({ by: next ? [next, ...by.slice(1)] : null })}
              value={by[0] ?? ''}
            />
          </Shelf>
        ) : null}
        {thenBy ? (
          <Shelf accepts={isDimension} holds={dimensionField(by[1])} label="Dimension 2" onField={(field) => field.ref !== by[1] && set({ by: field.ref === by[0] ? (by[1] ? [by[1], by[0]] : [field.ref]) : [by[0], field.ref] })} set={Boolean(by[1])}>
            <Picker choices={[['', '(none)'], ...dimensions.filter(([name]) => name !== by[0])]} disabled={editor.pending} label="Dimension 2" onChange={(next) => set({ by: next ? [by[0], next] : [by[0]] })} value={by[1] ?? ''} />
          </Shelf>
        ) : null}
        {kind === 'table' && by[0] ? (
          <Shelf accepts={(field) => isDimension(field) && !by.includes(field.ref)} holds={dimensionField(typeof block.across === 'string' ? block.across : undefined)} label="Columns" onField={(field) => set({ across: field.ref })} set={typeof block.across === 'string'}>
            <Picker choices={[['', '(none)'], ...dimensions.filter(([name]) => !by.includes(name))]} disabled={editor.pending} label="Columns" onChange={(next) => set({ across: next || null })} value={typeof block.across === 'string' ? block.across : ''} />
          </Shelf>
        ) : null}
        {family === 'time' && ['line', 'area', 'column'].includes(kind) ? (
          <Shelf accepts={isDimension} holds={dimensionField(typeof block.axis === 'string' ? block.axis : undefined)} label="Along the axis" onField={(field) => set({ axis: field.ref, by: [...by.filter((entry) => entry !== field.ref && entry !== block.axis), field.ref], grain: null })} set={typeof block.axis === 'string'}>
            <Picker
              choices={[['', 'Dates'], ...dimensions]}
              disabled={editor.pending}
              label="Along the axis"
              onChange={(next) => {
                const series = by.filter((entry) => entry !== block.axis && entry !== next);
                set(next ? { axis: next, by: [...series, next], grain: null } : { axis: null, by: series.length ? series : null });
              }}
              value={typeof block.axis === 'string' ? block.axis : ''}
            />
          </Shelf>
        ) : null}
        {family === 'time' && kind !== 'calendar' && !block.axis ? <Choice choices={GRAINS} disabled={editor.pending} inherit="day" label="Grain" onChange={(grain) => set({ grain })} value={String(block.grain ?? 'day')} /> : null}
        {family !== 'prose' ? (
          <>
            <Choice
              choices={[...(WINDOWS.some(([id]) => id === (block.window ?? LENS)) ? WINDOWS : [...WINDOWS, [String(block.window), presetLabel(String(block.window))] as [string, string]]), [CUSTOM, 'Custom dates…']]}
              disabled={editor.pending}
              inherit={LENS}
              label="Period"
              onChange={(window) => (window === CUSTOM ? setCustomDates(true) : set({ window: window === LENS ? null : window }))}
              value={customDates ? CUSTOM : String(block.window ?? LENS)}
            />
            {customDates || FIXED.test(String(block.window ?? '')) ? (
              <FixedDates
                disabled={editor.pending}
                onApply={(window) => {
                  set({ window });
                  setCustomDates(false);
                }}
                window={typeof block.window === 'string' ? block.window : undefined}
              />
            ) : null}
          </>
        ) : null}
        <Choice choices={compareChoices} disabled={editor.pending} inherit={LENS} label="Compared with" onChange={(next) => next !== compare && set({ compare: next === LENS ? null : next === 'none' ? 'none' : next.split(',') })} value={compare} />
        <Shelf accepts={(field) => isDimension(field) || field.kind === 'number'} label="Conditions" onField={(field) => setWhereColumn(field.column && field.kind === 'number' ? field.column : field.ref)} set={Object.keys(where).length > 0} stacked>
          <Stack spacing={0.5} sx={{ width: '100%' }}>
            {Object.entries(where).length ? (
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {Object.entries(where).map(([column, condition]) => (
                  <Chip
                    deleteIcon={<CloseIcon aria-label={`Remove the ${columnLabel(column)} condition`} />}
                    key={column}
                    label={conditionText(columnLabel(column), condition)}
                    onDelete={editor.pending ? undefined : () => set({ where: Object.keys(where).length > 1 ? { [column]: null } : null })}
                    size="small"
                    sx={{ fontSize: 12.5, height: 'auto', maxWidth: '100%', py: 0.25, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                  />
                ))}
              </Stack>
            ) : null}
            <Reveal label="Add a condition" open={Boolean(whereColumn)}>
              {(close) => (
                <WhereAdder
                  columns={dimensions}
                  disabled={editor.pending}
                  initialColumn={whereColumn}
                  key={whereColumn}
                  numbers={numberColumns}
                  onAdd={(column, condition) => {
                    set({ where: { ...where, [column]: condition } });
                    setWhereColumn('');
                    close();
                  }}
                  options={data?.options}
                />
              )}
            </Reveal>
          </Stack>
        </Shelf>
        {state && data ? (
          <Box>
            <Button aria-expanded={showingSql} onClick={() => setShowingSql(!showingSql)} size="small" startIcon={<CodeOutlinedIcon sx={{ fontSize: '16px !important' }} />} sx={{ fontSize: 12.5, minWidth: 0, px: 0.75, textTransform: 'none' }} variant="text">
              {showingSql ? 'Hide SQL' : 'Show SQL'}
            </Button>
            {showingSql ? <BlockSql blockId={block.id} lensId={data.lensId} lensVersion={data.lensVersion} state={state} /> : null}
          </Box>
        ) : null}
      </Section>
      {between}
      <Section id="fields" summary={`${all.length} fields`} title="Fields">
        <FieldList
          datasetLabels={(id) => datasets.find((dataset) => dataset.id === id)?.labels}
          editor={editor}
          fields={all}
          onPick={pick}
          used={used}
        />
      </Section>
      <Menu anchorEl={aggregating?.anchor} onClose={() => setAggregating(undefined)} open={Boolean(aggregating)}>
        {AGGREGATES.map(([id, label]) => (
          <MenuItem
            dense
            key={id}
            onClick={() => {
              if (aggregating) {
                const made = measureFromNumber(aggregating.field, id, taken);
                editor.apply([made.operation, ...aggregating.place(made.id)]);
              }
              setAggregating(undefined);
            }}
          >
            {`${label} of ${aggregating?.field.label ?? ''}`}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
